/**
 * @file AppProvider - React context for App / FrontendService lifecycle.
 *
 * Provides the root app instance to the React tree and manages its
 * lifecycle (start/stop) based on component mount state. The default
 * export name is `AppProvider` / `useApp`; the older
 * `ReactProvider` / `useService` names are kept as deprecated aliases
 * for in-flight workspace code.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { CoreError } from '../../shared/index.ts'
import type { FrontendService } from '../frontend_service.ts'

////////////////////////////////////////////////////////////////////////////////
// Context
////////////////////////////////////////////////////////////////////////////////

/**
 * Context value shape.
 */
type ServiceContextValue<T extends FrontendService> = {
  /** The root service instance. */
  service: T

  /** Whether the service is currently running. */
  running: boolean

  /** Any error that occurred during startup. */
  error: Error | null
}

// Create context with undefined default (must be used within provider)
const ServiceContext = createContext<
  ServiceContextValue<FrontendService> | undefined
>(undefined)

////////////////////////////////////////////////////////////////////////////////
// Provider
////////////////////////////////////////////////////////////////////////////////

/**
 * Props for {@link AppProvider} / {@link ReactProvider}.
 */
export type AppProviderProps<T extends FrontendService> = {
  /** Factory function that creates the app/service instance. */
  create: () => T

  /** Children to render. */
  children: ReactNode
}

/** @deprecated Renamed to {@link AppProviderProps}. */
export type ReactProviderProps<T extends FrontendService> = AppProviderProps<T>

/**
 * AppProvider — manages an `App` (or any FrontendService) lifecycle in React.
 *
 * Creates the instance on mount, calls `configure()` + `start()`, gates
 * the children render on a clean startup (`running` *or* `error`), and
 * calls `stop()` on unmount. Provides the live instance to descendants
 * via context — read with {@link useApp}.
 *
 * @example
 * ```tsx
 * function Root() {
 *   return (
 *     <AppProvider create={() => new MyApp({ baseUrl })}>
 *       <MyRoutes />
 *     </AppProvider>
 *   )
 * }
 * ```
 */
export function AppProvider<T extends FrontendService>({
  create,
  children,
}: AppProviderProps<T>): ReactNode {
  // Use ref to hold service instance (survives re-renders)
  // We use a function to lazily initialize only once
  const serviceRef = useRef<T | null>(null)

  // Lazily create service once
  if (serviceRef.current === null) {
    serviceRef.current = create()
  }

  // Now we know service is definitely not null
  const service = serviceRef.current

  // Track running state
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Cache the in-flight lifecycle promise across effect cycles. React 18+
  // StrictMode in dev fires the setup/cleanup pair twice; a service has
  // a one-shot lifecycle (configure→start) and its `configure()` guards
  // `state === 'created'`, so a naive second-cycle invocation would throw
  // `INVALID_STATE`. Storing the chain on a ref lets the second cycle
  // await the same promise instead of restarting the lifecycle.
  const lifecycleRef = useRef<Promise<void> | null>(null)

  // Manage lifecycle
  useEffect(() => {
    let cancelled = false
    const svc = service // capture for closure

    if (lifecycleRef.current === null) {
      // First effect cycle (or first cycle after a real remount): kick off
      // configure → start. Mirrors `App.run()` for the imperative path.
      lifecycleRef.current = (async () => {
        await svc.configure()
        await svc.start()
      })()
    }

    lifecycleRef.current
      .then(() => {
        if (!cancelled) setRunning(true)
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      })

    return () => {
      cancelled = true
      // `stop()` is a no-op unless the service is `running`. During the
      // StrictMode synthetic cleanup the service is still `configuring`
      // or `starting`, so this is a no-op there; on the real unmount the
      // service is `running` and stop fires properly.
      svc.stop().catch(() => {
        // Ignore stop errors
      })
    }
  }, [service])

  // Provide context
  const value: ServiceContextValue<T> = { service, running, error }

  // Hold rendering until lifecycle settles (running OR error). Without
  // this, children render on the first React pass — before `start()`'s
  // async work (e.g. `/account/me`) resolves — so any subtree that
  // reads service state in a mount-time effect sees the un-hydrated
  // shape and acts on it. Concretely: lab's route guards mis-bounced
  // signed-in learners to `/authorize?next=…` because `account.learner`
  // was still null on the boot pass. Errors still flow through `value`
  // so consumers can render a failure surface; the gate is loading-only.
  if (!running && !error) return null

  return (
    <ServiceContext.Provider
      value={value as ServiceContextValue<FrontendService>}>
      {children}
    </ServiceContext.Provider>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Hook
////////////////////////////////////////////////////////////////////////////////

/**
 * Access the root app/service instance from context.
 *
 * @throws CoreError with code 'MISSING_PROVIDER' if used outside
 *   {@link AppProvider}.
 */
export function useApp<T extends FrontendService>(): ServiceContextValue<T> {
  const context = useContext(ServiceContext)
  if (context === undefined) {
    throw new CoreError({
      code: 'MISSING_PROVIDER',
      message: 'useApp must be used within an AppProvider',
    })
  }
  return context as ServiceContextValue<T>
}

/**
 * @deprecated Renamed to {@link AppProvider}. The two are the same
 * function; this alias stays until every workspace migrates.
 */
export const ReactProvider = AppProvider

/**
 * @deprecated Renamed to {@link useApp}.
 */
export const useService = useApp
